import {
  AimdocServicesClient,
  type AimdocJobPlan,
  type AimdocJobStatusFrame,
  type AimdocServicesSessionResponse,
} from '@aimdoc/sdk';
import type { RunProgressItem, RunStatus } from '../components/onboarding/types';

/** GET /users/me/aimdoc-services-identity: the dashboard user as an SDK subject. */
export interface ServicesIdentityExchange {
  publishable_key: string | null;
  assertion: string | null;
}

const errorMessage = async (response: Response) => {
  try {
    const payload = await response.json();
    const detail = payload?.detail;
    if (typeof detail === 'string') return detail;
    if (detail?.message) return String(detail.message);
  } catch {
    // Edge/proxy errors are not guaranteed to be JSON.
  }
  return `Request failed (${response.status}).`;
};

/** POST /sdk/v1/session with a one-time identity assertion minted by the dashboard API. */
export async function exchangeServicesSdkSession(
  apiUrl: string,
  identity: ServicesIdentityExchange,
  signal?: AbortSignal
): Promise<AimdocServicesSessionResponse> {
  if (!identity.publishable_key || !identity.assertion) {
    throw new Error('Aimdoc Services identity is not configured.');
  }
  const response = await fetch(`${apiUrl.replace(/\/$/, '')}/sdk/v1/session`, {
    method: 'POST',
    credentials: 'omit',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      publishable_key: identity.publishable_key,
      identity_assertion: identity.assertion,
    }),
    signal,
  });
  if (!response.ok) throw new Error(await errorMessage(response));
  return (await response.json()) as AimdocServicesSessionResponse;
}

/**
 * The public `@aimdoc/sdk` client, pointed at this deployment's API and fed
 * sessions from the dashboard's identity endpoint, so the build screen
 * follows the onboarding job exactly the way a customer's product would
 * (`jobs.subscribe`). Each session exchange fetches a fresh assertion: every
 * assertion carries a one-time jti.
 */
export function createOnboardingJobsClient(
  apiUrl: string,
  getIdentity: () => Promise<ServicesIdentityExchange>
): AimdocServicesClient {
  return new AimdocServicesClient({
    apiUrl: apiUrl || undefined,
    exchangeSession: async () => exchangeServicesSdkSession(apiUrl, await getIdentity()),
  });
}

/** `plan` frame → build-screen checklist rows (same mapping as the server's summarize). */
export function progressFromPlan(plan: Pick<AimdocJobPlan, 'items'> | null | undefined): RunProgressItem[] | null {
  const items = Array.isArray(plan?.items) ? plan!.items : null;
  if (!items) return null;
  return items
    .filter((item): item is AimdocJobPlan['items'][number] => Boolean(item) && typeof item === 'object')
    .map((item) => ({
      key: String(item.id || ''),
      label: String(item.title || ''),
      state:
        item.status === 'in_progress'
          ? 'active'
          : item.status === 'done' || item.status === 'skipped'
            ? 'done'
            : 'todo',
      detail: typeof item.note === 'string' ? item.note : null,
    }));
}

const WAKE_TO_RUN_STATUS: Record<string, RunStatus> = {
  pending: 'queued',
  queued: 'queued',
  running: 'running',
  waiting: 'waiting',
  waiting_input: 'waiting',
  succeeded: 'completed',
  failed: 'failed',
  cancelled: 'cancelled',
  skipped: 'cancelled',
};

/** `wake` frame status (or a `status` frame's wake_status) → build-screen status. */
export function runStatusFromWake(status: string | null | undefined): RunStatus | null {
  return status ? WAKE_TO_RUN_STATUS[status] ?? null : null;
}

/**
 * `status` frame → build-screen status. A job paused on the owner's input is
 * `waiting` whatever its wake says; otherwise the active wake's status wins,
 * then plain working/idle.
 */
export function runStatusFromJobStatus(frame: Pick<AimdocJobStatusFrame, 'blocked_on' | 'working' | 'wake_status'>): RunStatus | null {
  if (frame.blocked_on === 'owner_input') return 'waiting';
  const fromWake = runStatusFromWake(frame.wake_status);
  if (fromWake) return fromWake;
  return frame.working ? 'running' : null;
}
